import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import {
  ArrowLeft, Mail, ExternalLink, Headset, Lock, CheckCircle2, RotateCcw,
  SendHorizontal, StickyNote, Clock3, AlertCircle, MessageSquare, Hash,
  UserRound, History, SlidersHorizontal, Paperclip, X, FileText, Download, Film, Loader2,
  Copy, Check, Pencil, Wrench, Sparkles, Hand, ChevronDown, Merge, MailWarning,
} from 'lucide-react';
import { MergeTicketsDialog } from '@/components/MergeTicketsDialog';
import {
  getTicket, getMessages, getEvents, ensureConversation, postMessage, editMessage,
  setTicketStatus, setTicketPriority, saveInternalNote, setTicketTag,
  fetchReadCursor, firstUnreadCustomerMessageId, markConversationRead, markConversationUnread, subscribeToThread, currentAgent, canEditMessage,
  uploadAttachment, attachmentKind, attachmentDownloadUrl, formatAttachmentSize,
  ticketTitle, ticketSubtitle, firstResponseMinutes, formatDuration,
  TICKET_STATUSES, TICKET_PRIORITIES, TICKET_TAGS, STATUS_LABELS, PRIORITY_LABELS, TYPE_LABELS, TAG_LABELS,
  STATUS_CLASSES, PRIORITY_CLASSES, TYPE_CLASSES, SOURCE_CLASSES, TAG_CLASSES,
  QUEUE_LABELS, ticketQueue,
  MAX_ATTACHMENT_BYTES, MAX_ATTACHMENTS_PER_MESSAGE,
  takeOverFromAi, listMergedTickets, getCreatorFullName, AI_HANDOFF_REASON_LABELS, AI_STATUS_LABELS, AI_MAX_REPLIES_PER_TICKET,
  type TicketStatus, type TicketPriority, type TicketTag, type SupportMessageRow, type TicketEventRow,
  type SupportAttachment,
} from '@/lib/support'; 

// Admin ticket detail — the /ticket-chat/:id route the Support Tickets queue
// opens. The conversation is a fixed-height card of its own (own internal
// scroll) with everything an agent needs to decide and act on laid out BELOW
// it — not beside it. A side-by-side grid stretches BOTH columns to the
// taller one's height (CSS grid rows default to equal-height), so with four
// info cards next to it the chat panel used to inherit their combined height
// and the whole page went comically long. Stacking avoids that outright: the
// chat gets its own bounded height no matter how much sits underneath it.
//
// The thread is the SAME one the creator or brand user sees. A ticket that
// predates this feature has no thread yet; the page materializes one from the
// ticket's original message on open, which is what makes the queue uniform
// across both sources.

// Radix Select can't take an empty-string item value, so "no tag" needs its
// own sentinel distinct from any real TicketTag.
const NO_TAG = '__none__';

const EVENT_LABELS: Record<string, string> = {
  created: 'Ticket created',
  chat_started: 'Chat opened',
  status_changed: 'Status changed',
  assigned: 'Assigned',
  unassigned: 'Unassigned',
  priority_changed: 'Priority changed',
  reopened: 'Reopened by customer',
  merged: 'Merged into',
  merged_in: 'Merged in',
};

function eventDetail(e: TicketEventRow): string {
  const label = (v: string | null) => {
    if (!v) return '—';
    return STATUS_LABELS[v as TicketStatus] ?? PRIORITY_LABELS[v as TicketPriority] ?? TYPE_LABELS[v as keyof typeof TYPE_LABELS] ?? v;
  };
  switch (e.event_type) {
    case 'status_changed':
    case 'reopened':
    case 'priority_changed':
      return `${label(e.from_value)} → ${label(e.to_value)}`;
    case 'assigned':
      return e.to_value ?? '';
    case 'unassigned':
      return e.from_value ?? '';
    case 'created':
      return label(e.to_value);
    case 'merged':
      return e.to_value ?? '';
    case 'merged_in':
      return `${e.from_value ?? ''}${e.to_value ? ` · ${e.to_value} message${e.to_value === '1' ? '' : 's'} moved` : ''}`;
    default:
      return '';
  }
}

/** "David D." / "david@x.com" → "DD" / "D" — the customer avatar's fallback
 *  glyph. Never empty: falls back to "?" so a nameless legacy row still gets a
 *  circle instead of a blank one. */
function initials(name: string | null | undefined): string {
  const s = (name || '').trim();
  if (!s) return '?';
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Day separators, so a long thread reads chronologically at a glance. */
function groupByDay(messages: SupportMessageRow[]) {
  const groups: { day: string; label: string; messages: SupportMessageRow[] }[] = [];
  for (const m of messages) {
    const day = new Date(m.created_at).toDateString();
    const last = groups[groups.length - 1];
    if (last?.day === day) last.messages.push(m);
    else groups.push({ day, label: format(new Date(m.created_at), 'EEEE, MMM d, yyyy'), messages: [m] });
  }
  return groups;
}

/** Marks the first and last message of each "run" (consecutive messages from
 *  the same sender). The run start opens the vertical gap; the run end is the
 *  one that carries the avatar, the tail corner and the visible timestamp —
 *  the iMessage grouping that keeps a reply burst from reading as noise. */
function withRuns(messages: SupportMessageRow[], unreadFromId: string | null): { m: SupportMessageRow; isRunStart: boolean; isRunEnd: boolean }[] {
  // The "New messages" line breaks the run: its message gets a key of its own,
  // so it starts a fresh group and the message before it ends one. is_ai is
  // part of the key too so an AI reply and a human agent reply never share a
  // run — each needs its own avatar/badge even back-to-back.
  const keys = messages.map((m) => (m.sender_type === 'system' ? null : m.id === unreadFromId ? `unread:${m.id}` : `${m.sender_type}:${m.is_internal}:${m.is_ai}`));
  return messages.map((m, i) => ({
    m,
    isRunStart: keys[i] === null || keys[i] !== keys[i - 1],
    isRunEnd: keys[i] === null || keys[i] !== keys[i + 1],
  }));
}

/** One attachment inside a message bubble: an inline thumbnail for images
 *  (click opens the full-size lightbox), a small downloadable card for
 *  everything else. Mirrors the creator app's rendering so the same message
 *  looks like the same message on both sides. */
function AttachmentPreview({ a, onOpen }: { a: SupportAttachment; onOpen: () => void }) {
  const kind = attachmentKind(a);
  if (kind === 'image') {
    const w = a.width && a.height ? Math.min(220, a.width) : 200;
    const h = a.width && a.height ? Math.round(w * (a.height / a.width)) : 160;
    return (
      <button
        type="button"
        onClick={onOpen}
        className="group/img relative block overflow-hidden rounded-xl ring-1 ring-inset ring-black/10"
        style={{ width: w, height: Math.min(h, 260) }}
      >
        <img
          src={a.url}
          alt={a.name || 'Attachment'}
          className="h-full w-full object-cover transition-transform duration-300 group-hover/img:scale-[1.03]"
        />
        <span className="absolute inset-0 bg-black/0 transition-colors group-hover/img:bg-black/15" />
      </button>
    );
  }
  const Icon = kind === 'pdf' ? FileText : kind === 'video' ? Film : FileText;
  return (
    <a
      href={attachmentDownloadUrl(a)}
      target="_blank"
      rel="noreferrer"
      className="flex max-w-[240px] items-center gap-2 rounded-xl border border-border/60 bg-background/70 px-2.5 py-2 text-xs transition-colors hover:bg-background"
    >
      <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-white', kind === 'pdf' ? 'bg-red-500' : 'bg-slate-500')}>
        <Icon className="h-3.5 w-3.5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-foreground">{a.name || 'File'}</span>
        <span className="block text-muted-foreground">{formatAttachmentSize(a.size)}</span>
      </span>
      <Download className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
    </a>
  );
}

export default function TicketChat() {
  const { id } = useParams<{ id: string }>();
  const ticketId = String(id);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Back goes through in-app history when there is any, so the Support
  // Tickets queue's filters/search/page — mirrored into its own URL — are
  // still there. location.key === 'default' means no in-app history (a
  // pasted link, a refresh, a new tab), so fall back to a fresh /feedback.
  // Same pattern as HireDetail.tsx's backToPayments.
  const cameFromApp = location.key !== 'default';
  const backToTickets = useCallback(
    () => (cameFromApp ? navigate(-1) : navigate('/feedback')),
    [cameFromApp, navigate],
  );

  // Unsent reply survives leaving the page, per ticket. localStorage can throw
  // (private mode, blocked site data), so every access is guarded.
  const draftKey = `ticket-chat-draft:${ticketId}`;
  const readDraft = (key: string) => {
    try { return localStorage.getItem(key) ?? ''; } catch { return ''; }
  };
  const writeDraft = (key: string, value: string) => {
    try {
      if (value) localStorage.setItem(key, value);
      else localStorage.removeItem(key);
    } catch { /* draft is a convenience only */ }
  };
  const [reply, setReply] = useState(() => readDraft(draftKey));
  const updateReply = (value: string) => { setReply(value); writeDraft(draftKey, value); };
  // The route can swap ticket ids without remounting this page.
  useEffect(() => { setReply(readDraft(draftKey)); }, [draftKey]);
  const [isInternal, setIsInternal] = useState(false);
  const [note, setNote] = useState('');
  const [resolveNote, setResolveNote] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [imageViewer, setImageViewer] = useState<SupportAttachment | null>(null);
  useEffect(() => {
    if (!imageViewer) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setImageViewer(null); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [imageViewer]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  // Copy/edit only render on hover — every message carrying its own visible
  // icon pair read as button clutter, not a chat.
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  // The AI summary is agent-facing context, not conversation — collapsed by
  // default so it reads as a status line instead of competing with the
  // message canvas for the card's fixed height.
  const [aiSummaryOpen, setAiSummaryOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const threadEndRef = useRef<HTMLDivElement>(null);
  const newMessagesRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // ── Data ──────────────────────────────────────────────────────────────────
  const { data: ticket, isLoading: ticketLoading, error: ticketError } = useQuery({
    queryKey: ['support-ticket', ticketId],
    queryFn: () => getTicket(ticketId),
  });

  // Materialize the thread if this ticket doesn't have one. Idempotent, so
  // running it on every load is safe and keeps the two sides on one thread.
  const { data: conversationId } = useQuery({
    queryKey: ['support-conversation', ticketId, ticket?.conversation_id],
    queryFn: () => ticket?.conversation_id ?? ensureConversation(ticketId),
    enabled: !!ticket,
  });

  const { data: messages, isLoading: messagesLoading, isFetching: messagesFetching } = useQuery({
    queryKey: ['support-messages', conversationId],
    queryFn: () => getMessages(String(conversationId)),
    enabled: !!conversationId,
  });

  const { data: events } = useQuery({
    queryKey: ['support-events', ticketId],
    queryFn: () => getEvents(ticketId),
  });

  // Tickets merged into this one — labels the messages that moved in with the
  // ticket they were originally written on.
  const { data: mergedTickets } = useQuery({
    queryKey: ['support-merged-tickets', ticketId],
    queryFn: () => listMergedTickets(ticketId),
    enabled: (ticket?.merged_ticket_count ?? 0) > 0,
  });
  const mergedNumberById = useMemo(
    () => new Map((mergedTickets ?? []).map((t) => [t.id, t.ticket_number])),
    [mergedTickets],
  );

  // Creator tickets only: a brand ticket's requester is a brand, not a person.
  const { data: requesterFullName } = useQuery({
    queryKey: ['support-requester-full-name', ticket?.user_id],
    queryFn: () => getCreatorFullName(ticket!.user_id!),
    enabled: !!ticket?.user_id && ticket.source !== 'brand',
    staleTime: 5 * 60 * 1000,
  });
  console.log("🚀 ~ TicketChat ~ requesterFullName:", requesterFullName)

  // Gates the per-message edit button — an agent can only fix their OWN
  // reply, never a teammate's (see canEditMessage). Rarely changes, so this
  // is effectively a one-time fetch per session, not per ticket.
  const { data: agent } = useQuery({ queryKey: ['current-agent'], queryFn: currentAgent, staleTime: Infinity });

  useEffect(() => { if (ticket?.internal_note) setNote(ticket.internal_note); }, [ticket?.internal_note]);

  // Opening the ticket clears its unread count in the queue badge. The read
  // cursor is captured first, because this write is what advances it and the
  // "New messages" line needs the value from before.
  const [readCursor, setReadCursor] = useState<{ conversationId: string; at: string | null } | null>(null);
  // Set once the open-marks-read write above has landed, so "Mark as unread"
  // can't race it and be overwritten a moment later.
  const [readSyncedFor, setReadSyncedFor] = useState<string | null>(null);
  useEffect(() => {
    if (!conversationId) return;
    const convId = String(conversationId);
    let alive = true;
    (async () => {
      const at = await fetchReadCursor(convId).catch(() => null);
      if (alive) setReadCursor({ conversationId: convId, at });
      await markConversationRead(convId);
      if (alive) setReadSyncedFor(convId);
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
      queryClient.invalidateQueries({ queryKey: ['support-awaiting-count'] });
    })();
    return () => { alive = false; };
  }, [conversationId, queryClient]);

  // Where the "New messages" line goes. Worked out once per conversation, as
  // soon as the cursor is known and the message list is FRESH (a cached list
  // from an earlier visit would miss the very messages that are new), then
  // held, so the line stays put while you read and live replies don't move it.
  const [unreadFromId, setUnreadFromId] = useState<string | null>(null);
  const unreadResolvedFor = useRef<string | null>(null);
  useEffect(() => {
    setUnreadFromId(null);
    unreadResolvedFor.current = null;
  }, [conversationId]);
  useEffect(() => {
    const convId = conversationId ? String(conversationId) : null;
    if (!convId || !messages || messagesFetching || readCursor?.conversationId !== convId) return;
    if (unreadResolvedFor.current === convId) return;
    unreadResolvedFor.current = convId;
    setUnreadFromId(firstUnreadCustomerMessageId(messages, readCursor.at));
  }, [conversationId, messages, messagesFetching, readCursor]);

  // "Mark as unread": puts the newest unanswered customer messages back to
  // unread for THIS agent (see markConversationUnread), then leaves the page —
  // the page only marks a ticket read when it opens, so staying would be fine,
  // but going back to the queue is where the unread count is visible.
  const [markingUnread, setMarkingUnread] = useState(false);
  const markUnread = useCallback(async () => {
    if (!conversationId) return;
    setMarkingUnread(true);
    try {
      const marked = await markConversationUnread(String(conversationId));
      if (!marked) {
        toast.info('No customer messages to mark as unread');
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
      toast.success('Marked as unread');
      backToTickets();
    } catch (e: any) {
      toast.error(e?.message || 'Could not mark as unread');
    } finally {
      setMarkingUnread(false);
    }
  }, [conversationId, queryClient, backToTickets]);
  const hasCustomerMessage = !!messages?.some((m) => m.sender_type === 'customer');

  // Realtime: a customer reply appears without a refresh. Refetches the ticket
  // too, since a reply can reopen a resolved ticket.
  const refetchThread = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['support-messages', conversationId] });
    queryClient.invalidateQueries({ queryKey: ['support-ticket', ticketId] });
    queryClient.invalidateQueries({ queryKey: ['support-events', ticketId] });
  }, [queryClient, conversationId, ticketId]);

  useEffect(() => {
    if (!conversationId) return;
    return subscribeToThread(String(conversationId), refetchThread);
  }, [conversationId, refetchThread]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ block: 'end' });
  }, [messages?.length]);

  // Once the line exists, start the thread there instead of at the bottom.
  useEffect(() => {
    if (unreadFromId) newMessagesRef.current?.scrollIntoView({ block: 'start' });
  }, [unreadFromId]);

  // Auto-grow the composer up to its max-height, so a long reply doesn't get
  // typed through a two-line slot.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${el.scrollHeight}px`;
  }, [reply]);

  // ── Mutations ─────────────────────────────────────────────────────────────
  const invalidateAll = () => {
    refetchThread();
    queryClient.invalidateQueries({ queryKey: ['feedback'] });
    queryClient.invalidateQueries({ queryKey: ['feedback-source-counts'] });
    queryClient.invalidateQueries({ queryKey: ['support-awaiting-count'] });
    queryClient.invalidateQueries({ queryKey: ['feedback-ai-queue-counts'] });
    queryClient.invalidateQueries({ queryKey: ['support-merged-tickets', ticketId] });
    queryClient.invalidateQueries({ queryKey: ['support-merge-candidates', ticketId] });
  };

  const sendMutation = useMutation({
    mutationFn: async (vars: { body: string; internal: boolean; files: File[] }) => {
      let attachments: SupportAttachment[] = [];
      if (vars.files.length > 0) {
        setUploading(true);
        try {
          attachments = await Promise.all(vars.files.map((f) => uploadAttachment(String(conversationId), f)));
        } finally {
          setUploading(false);
        }
      }
      return postMessage(String(conversationId), ticketId, vars.body, vars.internal, attachments);
    },
    onSuccess: (_d, vars) => {
      updateReply('');
      setPendingFiles([]);
      invalidateAll();
      toast.success(vars.internal ? 'Internal note added' : 'Reply sent to the customer');
    },
    onError: (e: any) => toast.error(e.message || 'Could not send'),
  });

  const editMutation = useMutation({
    mutationFn: (vars: { messageId: string; oldBody: string; newBody: string }) =>
      editMessage(vars.messageId, vars.oldBody, vars.newBody),
    onSuccess: () => {
      setEditingId(null);
      setEditDraft('');
      refetchThread();
    },
    onError: (e: any) => toast.error(e.message || 'Could not save the edit'),
  });

  // ── Copy / edit ───────────────────────────────────────────────────────────
  const copyMessage = async (m: SupportMessageRow) => {
    try {
      await navigator.clipboard.writeText(m.body);
      setCopiedId(m.id);
      setTimeout(() => setCopiedId((id) => (id === m.id ? null : id)), 1500);
    } catch {
      toast.error('Could not copy — clipboard access was denied');
    }
  };

  const startEdit = (m: SupportMessageRow) => { setEditingId(m.id); setEditDraft(m.body); };
  const cancelEdit = () => { setEditingId(null); setEditDraft(''); };
  const saveEdit = (m: SupportMessageRow) => {
    const body = editDraft.trim();
    if (!body || body === m.body) { cancelEdit(); return; }
    if (!canEditMessage(m, agent?.id)) {
      toast.error('This message can no longer be edited (past the 5-minute window)');
      return;
    }
    editMutation.mutate({ messageId: m.id, oldBody: m.body, newBody: body });
  };

  // ── Attachments ───────────────────────────────────────────────────────────
  const handleFilesSelected = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const incoming = Array.from(files);
    if (pendingFiles.length + incoming.length > MAX_ATTACHMENTS_PER_MESSAGE) {
      toast.error(`You can attach up to ${MAX_ATTACHMENTS_PER_MESSAGE} files per message.`);
      return;
    }
    const accepted: File[] = [];
    for (const f of incoming) {
      if (f.size > MAX_ATTACHMENT_BYTES) {
        toast.error(`${f.name} is over 25 MB.`);
        continue;
      }
      accepted.push(f);
    }
    if (accepted.length) setPendingFiles((prev) => [...prev, ...accepted]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePendingFile = (index: number) => setPendingFiles((prev) => prev.filter((_, i) => i !== index));

  const statusMutation = useMutation({
    mutationFn: (vars: { to: TicketStatus; note?: string }) =>
      setTicketStatus(ticketId, ticket!.status, vars.to, vars.note),
    onSuccess: (_d, vars) => {
      setResolveNote('');
      invalidateAll();
      toast.success(`Ticket marked ${STATUS_LABELS[vars.to]}`);
    },
    onError: (e: any) => toast.error(e.message || 'Could not change status'),
  });

  const priorityMutation = useMutation({
    mutationFn: (to: TicketPriority) => setTicketPriority(ticketId, ticket!.priority, to),
    onSuccess: () => { invalidateAll(); toast.success('Priority updated'); },
    onError: (e: any) => toast.error(e.message || 'Could not change priority'),
  });

  const noteMutation = useMutation({
    mutationFn: (n: string) => saveInternalNote(ticketId, ticket?.internal_note ?? null, n),
    onSuccess: () => { invalidateAll(); toast.success('Note saved'); },
    onError: (e: any) => toast.error(e.message || 'Could not save note'),
  });

  const tagMutation = useMutation({
    mutationFn: (tag: TicketTag | null) => setTicketTag(ticketId, ticket?.tags ?? [], tag),
    onSuccess: (_d, tag) => {
      invalidateAll();
      toast.success(tag ? `Tagged "${TAG_LABELS[tag]}"` : 'Tag cleared');
    },
    onError: (e: any) => toast.error(e.message || 'Could not update the tag'),
  });

  // Stops BMC Assistant before it replies again — the ticket moves to Needs
  // human until an agent answers. Replying directly also takes over.
  const takeOverMutation = useMutation({
    mutationFn: () => takeOverFromAi(ticketId),
    onSuccess: (applied) => {
      invalidateAll();
      if (applied) toast.success('You have the ticket — BMC Assistant has stopped replying');
      else toast.info('BMC Assistant had already handed this ticket off');
    },
    onError: (e: any) => toast.error(e.message || 'Could not take over this ticket'),
  });

  const dayGroups = useMemo(() => groupByDay(messages ?? []), [messages]);
  const sla = ticket ? firstResponseMinutes(ticket) : null;

  // Source-tinted customer avatar — ties the chat's visual identity to the
  // same purple/amber language the queue's source badges already use, rather
  // than an arbitrary colour.
  const customerGradient = ticket?.is_coach
    ? 'from-pink-500 to-rose-500'
    : ticket?.source === 'brand'
      ? 'from-amber-500 to-orange-500'
      : 'from-violet-500 to-fuchsia-500';

  // ── Loading / error ───────────────────────────────────────────────────────
  if (ticketLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-9 w-64" />
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
          <Skeleton className="h-[70vh] w-full rounded-xl" />
          <Skeleton className="h-[70vh] w-full rounded-xl" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-48 w-full rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (ticketError || !ticket) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
        <AlertCircle className="h-10 w-10 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          {(ticketError as any)?.message || 'This ticket could not be found.'}
        </p>
        <Button variant="outline" onClick={backToTickets}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Support Tickets
        </Button>
      </div>
    );
  }

  const isSending = sendMutation.isPending || uploading;
  const isMerged = !!ticket.merged_into_ticket_id;
  const canSend = (reply.trim().length > 0 || pendingFiles.length > 0) && !!conversationId && !isSending && !isMerged;
  const requesterName = ticket.display_name || ticket.email || 'Customer';
  // Mirrors the condition support_post_message uses to send this one message to
  // In Progress rather than Waiting on Customer — support opened the ticket and
  // hasn't said anything yet, so there is no customer question to hand back.
  const firstAgentMessagePending = ticket.opened_by_admin && ticket.status === 'open' && ticket.message_count === 0;

  return (
    // Capped and centred rather than edge-to-edge: a conversation reads badly
    // at 1300px of line length, and the whole page keeps one measure.
    <div className="mx-auto w-full max-w-[1180px] space-y-6">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <Button variant="ghost" size="icon" className="mt-0.5 shrink-0" onClick={backToTickets} aria-label="Back to Support Tickets">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 font-mono text-xs font-semibold tabular-nums text-muted-foreground">
                <Hash className="h-3 w-3" />{ticket.ticket_number ?? '—'}
              </span>
              <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${STATUS_CLASSES[ticket.status]}`}>
                {STATUS_LABELS[ticket.status]}
              </span>
              <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${PRIORITY_CLASSES[ticket.priority]}`}>
                {PRIORITY_LABELS[ticket.priority]}
              </span>
              <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${TYPE_CLASSES[ticket.type] || TYPE_CLASSES.general}`}>
                {TYPE_LABELS[ticket.type as keyof typeof TYPE_LABELS] || ticket.type}
              </span>
              <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${SOURCE_CLASSES[ticketQueue(ticket)]}`}>
                {QUEUE_LABELS[ticketQueue(ticket)]}
              </span>
              {ticket.opened_by_admin && (
                <Badge variant="outline" className="gap-1">
                  <Headset className="h-3 w-3" /> Opened by support
                </Badge>
              )}
              {isMerged && (
                <Badge variant="outline" className="gap-1">
                  <Merge className="h-3 w-3" /> Merged → #{ticket.merged_into_ticket_number ?? '—'}
                </Badge>
              )}
              {ticket.merged_ticket_count > 0 && (
                <Badge variant="outline" className="gap-1">
                  <Merge className="h-3 w-3" /> {ticket.merged_ticket_count} merged in
                </Badge>
              )}
              {ticket.tags?.includes('technical') && (
                <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${TAG_CLASSES.technical}`}>
                  <Wrench className="h-3 w-3" /> {TAG_LABELS.technical}
                </span>
              )}
              {ticket.awaiting_reply && (
                <Badge variant="destructive" className="gap-1">
                  <MessageSquare className="h-3 w-3" /> Awaiting reply
                </Badge>
              )}
              {ticket.ai_status && (
                <span className={cn(
                  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold',
                  ticket.ai_status === 'active'
                    ? 'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900 dark:bg-violet-950/50 dark:text-violet-300'
                    : ticket.ai_status === 'resolved'
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-300'
                      : 'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300',
                )}>
                  <Sparkles className="h-3 w-3" /> {AI_STATUS_LABELS[ticket.ai_status]}
                </span>
              )}
            </div>
            <h1 className="mt-1.5 truncate text-lg font-bold tracking-tight sm:text-xl" title={ticket.message}>
              {ticketTitle(ticket)}
            </h1>
            {ticketSubtitle(ticket) && (
              <p className="mt-0.5 truncate text-xs text-muted-foreground">
                Subject: <span className="font-medium text-foreground/80">{ticketSubtitle(ticket)}</span>
              </p>
            )}
          </div>
        </div>

        {/* Primary lifecycle actions — the two decisions an agent actually makes */}
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {isMerged ? (
            <Button onClick={() => navigate(`/ticket-chat/${ticket.merged_into_ticket_id}`)}>
              <ExternalLink className="mr-2 h-4 w-4" /> Open #{ticket.merged_into_ticket_number ?? '—'}
            </Button>
          ) : (<>
          {/* AI-handled tickets never show an unread count in the queue, so
              there is nothing to mark there. */}
          {conversationId && hasCustomerMessage && ticket.handled_by !== 'ai' && (
            <Button
              variant="outline"
              onClick={markUnread}
              disabled={markingUnread || readSyncedFor !== String(conversationId)}
              title="Show the newest customer messages as unread again (just for you)"
            >
              {markingUnread ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MailWarning className="mr-2 h-4 w-4" />}
              Mark as unread
            </Button>
          )}
          {ticket.status !== 'closed' && (
            <Button variant="outline" onClick={() => setMergeOpen(true)}>
              <Merge className="mr-2 h-4 w-4" /> Merge
            </Button>
          )}
          {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
            <Button
              onClick={() => statusMutation.mutate({ to: 'resolved', note: resolveNote })}
              disabled={statusMutation.isPending}
              className="shadow-sm"
            >
              <CheckCircle2 className="mr-2 h-4 w-4" /> Mark Resolved
            </Button>
          )}
          {ticket.status !== 'closed' ? (
            <Button
              variant="outline"
              onClick={() => statusMutation.mutate({ to: 'closed' })}
              disabled={statusMutation.isPending}
            >
              <Lock className="mr-2 h-4 w-4" /> Close
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => statusMutation.mutate({ to: 'in_progress' })}
              disabled={statusMutation.isPending}
            >
              <RotateCcw className="mr-2 h-4 w-4" /> Reopen
            </Button>
          )}
          </>)}
        </div>
      </div>

      {isMerged && (
        <Card className="flex flex-wrap items-center gap-2 border-slate-300 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-900/50">
          <Merge className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span>
            Merged into <span className="font-semibold">#{ticket.merged_into_ticket_number ?? '—'}</span>
            {ticket.merged_by_name && <> by <span className="font-semibold">{ticket.merged_by_name}</span></>}
            {ticket.merged_at && <> · {format(new Date(ticket.merged_at), 'MMM d, yyyy h:mm a')}</>}
            . Its messages now live in that ticket, and the customer no longer sees this one.
          </span>
        </Card>
      )}

      <MergeTicketsDialog
        open={mergeOpen}
        onOpenChange={setMergeOpen}
        primary={ticket}
        onMerged={() => invalidateAll()}
      />

      {/* ── Conversation + Manage, side by side. Manage is the one panel that
            earns a permanent spot next to the chat — the controls an agent
            reaches for while actually reading a reply (status, priority).
            Requester / Tracking / Activity are reference info you
            check occasionally, not mid-conversation, so they stay below.
            `items-stretch` (grid default) lets Manage fill the chat's own
            explicit height rather than the old bug's failure mode, where FOUR
            stacked cards forced the chat itself to grow to match them. One
            panel matching the chat's height on the right is the intended look
            here, not the same problem the other way round. ────────────────── */}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        {/* Plain card: solid surface, hairline border, no glass/glow. */}
        <Card
          className="flex flex-col overflow-hidden border-border bg-card p-0 shadow-sm"
          style={{ height: 'min(700px, 72vh)' }}
        >
          <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                <Headset className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold leading-tight tracking-tight">Conversation</p>
                <p className="truncate text-xs text-muted-foreground">
                  {(messages?.length ?? 0) || ticket.message_count} {((messages?.length ?? 0) || ticket.message_count) === 1 ? 'message' : 'messages'} with {requesterName}
                </p>
              </div>
            </div>
            {ticket.conversation_status === 'closed' ? (
              <Badge variant="secondary" className="shrink-0 gap-1">
                <Lock className="h-3 w-3" /> Customer can't reply
              </Badge>
            ) : (
              <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                </span>
                Live
              </span>
            )}
          </div>

          {/* BMC Assistant panel — who holds the ticket, why it was handed
              off, and the AI's note for the agent (never shown to the
              customer: admin-only table). Kept to a single compact row by
              default — the summary is a disclosure, not inline text — so it
              reads as a status strip instead of eating into the message
              canvas below it. */}
          {ticket.ai_status && (
            <div className={cn(
              'shrink-0 border-b px-4 py-1.5 text-sm',
              ticket.ai_awaiting_human
                ? 'border-rose-200 bg-rose-50/60 dark:border-rose-900/60 dark:bg-rose-950/20'
                : 'border-violet-200 bg-violet-50/50 dark:border-violet-900/60 dark:bg-violet-950/20',
            )}>
              <div className="flex min-h-7 flex-wrap items-center justify-between gap-2">
                <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
                  <span className="inline-flex items-center gap-1.5 font-semibold text-foreground">
                    <Sparkles className="h-3.5 w-3.5 shrink-0 text-violet-500" />
                    {ticket.ai_status === 'active'
                      ? 'BMC Assistant is answering'
                      : ticket.ai_status === 'resolved'
                        ? 'Resolved by BMC Assistant'
                        : ticket.ai_awaiting_human
                          ? 'Needs a human'
                          : 'Handed off by BMC Assistant'}
                  </span>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-muted-foreground tabular-nums">
                    {ticket.ai_reply_count}/{AI_MAX_REPLIES_PER_TICKET} replies
                    {ticket.ai_last_replied_at && ` · last ${formatDistanceToNow(new Date(ticket.ai_last_replied_at), { addSuffix: true })}`}
                  </span>
                  {ticket.ai_status === 'handed_off' && ticket.ai_handoff_reason && (
                    <>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-muted-foreground">
                        <span className="font-medium text-foreground">{AI_HANDOFF_REASON_LABELS[ticket.ai_handoff_reason]}</span>
                        {ticket.ai_handed_off_at && ` · ${formatDistanceToNow(new Date(ticket.ai_handed_off_at), { addSuffix: true })}`}
                      </span>
                    </>
                  )}
                  {ticket.ai_summary && (
                    <button
                      type="button"
                      onClick={() => setAiSummaryOpen((v) => !v)}
                      className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-medium text-violet-600 transition-colors hover:bg-violet-500/10 dark:text-violet-400"
                    >
                      {aiSummaryOpen ? 'Hide summary' : 'AI summary'}
                      <ChevronDown className={cn('h-3 w-3 transition-transform', aiSummaryOpen && 'rotate-180')} />
                    </button>
                  )}
                </div>
                {ticket.handled_by === 'ai' && ticket.ai_status === 'active' && (
                  <Button size="sm" variant="outline" className="h-6 shrink-0 gap-1.5 px-2 text-xs" onClick={() => takeOverMutation.mutate()} disabled={takeOverMutation.isPending}>
                    <Hand className="h-3 w-3" /> Take over
                  </Button>
                )}
              </div>
              {ticket.ai_summary && aiSummaryOpen && (
                <p className="mb-1.5 whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">
                  {ticket.ai_summary}
                </p>
              )}
            </div>
          )}

          {/* Message canvas — plain, no background pattern or glow. */}
          <div className="flex-1 overflow-y-auto bg-muted/20 px-4 py-3">
            {messagesLoading ? (
              <div className="space-y-4 pt-2">
                <Skeleton className="h-14 w-1/2 rounded-2xl" />
                <Skeleton className="ml-auto h-20 w-2/3 rounded-2xl" />
                <Skeleton className="h-14 w-2/5 rounded-2xl" />
              </div>
            ) : dayGroups.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-border bg-background">
                  <MessageSquare className="h-5 w-5 text-primary" />
                </span>
                <div className="space-y-0.5">
                  <p className="text-sm font-medium">No messages yet</p>
                  <p className="text-xs text-muted-foreground">Send the first reply to start the conversation.</p>
                </div>
              </div>
            ) : (
              dayGroups.map((group) => (
                <div key={group.day} className="pb-1">
                  {/* Sticky day chip — stays pinned while its own day scrolls
                      past, so you always know where you are in a long thread. */}
                  <div className="sticky top-0 z-20 flex justify-center py-2">
                    <span className="rounded-full border border-border bg-background px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {group.label}
                    </span>
                  </div>

                  {withRuns(group.messages, unreadFromId).map(({ m, isRunStart, isRunEnd }) => {
                    const newMessagesLine = m.id === unreadFromId && (
                      <div ref={newMessagesRef} role="separator" className="flex scroll-mt-12 items-center gap-3 pb-1 pt-4">
                        <span className="h-px flex-1 bg-primary/30" />
                        <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                          New messages
                        </span>
                        <span className="h-px flex-1 bg-primary/30" />
                      </div>
                    );
                    // System messages narrate the ticket; centred and unowned.
                    if (m.sender_type === 'system') {
                      return (
                        <Fragment key={m.id}>
                          {newMessagesLine}
                          <div className="flex justify-center py-2">
                            <div className="flex max-w-[85%] items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-center text-[11px] text-muted-foreground">
                              <History className="h-3 w-3 shrink-0 opacity-70" />
                              {m.body}
                              <span className="opacity-60">· {format(new Date(m.created_at), 'h:mm a')}</span>
                            </div>
                          </div>
                        </Fragment>
                      );
                    }

                    const isAgent = m.sender_type === 'agent';
                    const hasAttachments = (m.attachments?.length ?? 0) > 0;
                    const editable = canEditMessage(m, agent?.id);
                    const isEditing = editingId === m.id;
                    const isHovered = hoveredId === m.id;
                    return (
                      <Fragment key={m.id}>
                      {newMessagesLine}
                      <div
                        className={cn(
                          'flex items-end gap-2',
                          isAgent ? 'justify-end' : 'justify-start',
                          isRunStart ? 'pt-3' : 'pt-1',
                        )}
                        onMouseEnter={() => setHoveredId(m.id)}
                        onMouseLeave={() => setHoveredId((id) => (id === m.id ? null : id))}
                      >
                        {/* Avatar sits on the run's LAST bubble, where the eye
                            expects it with bottom-aligned rows. */}
                        {!isAgent && (
                          <Avatar className={cn('mb-4 h-6 w-6 shrink-0 ring-2 ring-background', !isRunEnd && 'invisible')}>
                            <AvatarFallback className={`bg-gradient-to-br ${customerGradient} text-[9px] font-bold text-white`}>
                              {initials(requesterName)}
                            </AvatarFallback>
                          </Avatar>
                        )}
                        <div className={cn('flex min-w-0 max-w-[76%] flex-col', isAgent ? 'items-end' : 'items-start')}>
                          {/* Internal notes are visible ONLY here — RLS keeps
                              them off the customer's thread entirely. */}
                          {m.merged_from_ticket_id && m.merged_from_ticket_id !== ticketId && (
                            <span className="mb-1 inline-flex items-center gap-0.5 text-[9px] font-semibold text-muted-foreground">
                              <Merge className="h-2.5 w-2.5" /> from #{mergedNumberById.get(m.merged_from_ticket_id) ?? '—'}
                            </span>
                          )}
                          {isRunStart && m.is_ai && (
                            <Badge variant="outline" className="mb-1 h-4 gap-0.5 border-violet-300 bg-violet-50 px-1 text-[9px] font-bold text-violet-700 dark:bg-violet-950/40 dark:text-violet-300">
                              <Sparkles className="h-2.5 w-2.5" /> BMC ASSISTANT
                            </Badge>
                          )}
                          {isRunStart && m.is_internal && (
                            <Badge variant="outline" className="mb-1 h-4 gap-0.5 border-amber-300 bg-amber-50 px-1 text-[9px] font-bold text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                              <StickyNote className="h-2.5 w-2.5" /> INTERNAL
                            </Badge>
                          )}
                          <div
                            title={format(new Date(m.created_at), 'EEEE, MMM d · h:mm a')}
                            className={cn(
                              'whitespace-pre-wrap break-words rounded-2xl text-sm leading-relaxed',
                              // Attachment-only bubbles get a slim frame instead
                              // of full text padding — matches the creator app.
                              hasAttachments && !m.body && !isEditing ? 'p-1.5' : 'px-3.5 py-2',
                              // Only the run's last bubble gets the tail corner.
                              isRunEnd && (isAgent ? 'rounded-br-md' : 'rounded-bl-md'),
                              // Flat, solid fills — the agent side reads as "us"
                              // through solid primary, not a glow effect.
                              m.is_internal
                                ? 'border border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-200'
                                : m.is_ai
                                  ? 'bg-violet-600 text-white'
                                  : isAgent
                                    ? 'bg-primary text-primary-foreground'
                                    : 'border border-border bg-muted text-foreground',
                            )}
                          >
                            {isEditing ? (
                              <div className="min-w-[180px]">
                                <Textarea
                                  autoFocus
                                  value={editDraft}
                                  onChange={(e) => setEditDraft(e.target.value.slice(0, 4000))}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(m); }
                                    if (e.key === 'Escape') cancelEdit();
                                  }}
                                  rows={2}
                                  className="min-h-0 resize-none border-none bg-black/10 p-2 text-sm text-inherit placeholder:text-inherit focus-visible:ring-1 dark:bg-white/10"
                                />
                                <div className="mt-1.5 flex justify-end gap-1.5">
                                  <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={cancelEdit} disabled={editMutation.isPending}>
                                    Cancel
                                  </Button>
                                  <Button size="sm" className="h-6 px-2 text-[11px]" onClick={() => saveEdit(m)} disabled={editMutation.isPending || !editDraft.trim()}>
                                    {editMutation.isPending ? 'Saving…' : 'Save'}
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <>
                                {/* Image/file first, caption below — matches WhatsApp. */}
                                {hasAttachments && (
                                  <div className={cn('flex flex-wrap gap-1.5', !!m.body && 'mb-2')}>
                                    {m.attachments.map((a, i) => (
                                      <AttachmentPreview key={i} a={a} onOpen={() => setImageViewer(a)} />
                                    ))}
                                  </div>
                                )}
                                {!!m.body && <p>{m.body}</p>}
                              </>
                            )}
                          </div>
                          {/* Every message gets its own timestamp — support
                              needs to see exactly when each one landed, not
                              just the last of a burst — plus copy/edit actions. */}
                          {!isEditing && (
                            <div className="mt-1 flex min-h-[18px] items-center gap-1.5 px-1">
                              <span className="text-[10px] tabular-nums text-muted-foreground">
                                {format(new Date(m.created_at), 'h:mm a')}{m.edited_at ? ' · edited' : ''}
                              </span>
                              {isHovered && (
                                <div className="flex items-center gap-0.5 rounded-full border border-border bg-popover px-1 py-0.5 shadow-sm">
                                  {!!m.body && (
                                    <button
                                      type="button"
                                      onClick={() => copyMessage(m)}
                                      title={copiedId === m.id ? 'Copied!' : 'Copy'}
                                      className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground"
                                    >
                                      {copiedId === m.id ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                                    </button>
                                  )}
                                  {editable && (
                                    <button
                                      type="button"
                                      onClick={() => startEdit(m)}
                                      title="Edit"
                                      className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground"
                                    >
                                      <Pencil className="h-3 w-3" />
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      </Fragment>
                    );
                  })}
                </div>
              ))
            )}
            <div ref={threadEndRef} />
          </div>

        {/* ── Composer ───────────────────────────────────────────────────── */}
        <div className="border-t border-border bg-background p-3">
          {isMerged && (
            <p className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Merge className="h-3.5 w-3.5 shrink-0" />
              This ticket was merged. Reply in #{ticket.merged_into_ticket_number ?? '—'} instead.
            </p>
          )}
          {/* <div className="mb-2 flex items-center justify-between gap-2">
             <div className="inline-flex items-center gap-0.5 rounded-full border border-border/60 bg-background p-1 shadow-sm">
              <button
                type="button"
                onClick={() => setIsInternal(false)}
                className={cn(
                  'flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-all',
                  !isInternal ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <SendHorizontal className="h-3 w-3" /> Reply
              </button>
              <button
                type="button"
                onClick={() => setIsInternal(true)}
                className={cn(
                  'flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-all',
                  isInternal ? 'bg-amber-500 text-white shadow-sm' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <StickyNote className="h-3 w-3" /> Note
              </button>
            </div>
            <p className="hidden text-[11px] text-muted-foreground sm:block">⌘/Ctrl + Enter to send</p>
          </div> */}

          {/* One bordered surface holds the attachments, the field and both
              buttons, so the composer reads as a single control rather than
              three floating ones. The focus ring lives on the shell. */}
          <div
            className={cn(
              'rounded-2xl border transition-colors',
              isInternal
                ? 'border-amber-300 bg-amber-50/60 focus-within:border-amber-400 dark:border-amber-800/60 dark:bg-amber-950/20'
                : 'border-border bg-background focus-within:border-primary/50',
            )}
          >
            {pendingFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 border-b border-border/50 p-2">
                {pendingFiles.map((f, i) => {
                  const isImg = f.type.startsWith('image/');
                  return (
                    <div key={`${f.name}-${i}`} className="relative">
                      {isImg ? (
                        <img
                          src={URL.createObjectURL(f)}
                          alt={f.name}
                          className="h-14 w-14 rounded-xl object-cover ring-1 ring-inset ring-border/70"
                        />
                      ) : (
                        <div className="flex h-14 max-w-[170px] items-center gap-2 rounded-xl bg-muted/70 px-2.5 ring-1 ring-inset ring-border/70">
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-500 text-white">
                            <FileText className="h-3.5 w-3.5" />
                          </span>
                          <span className="min-w-0">
                            <span className="block truncate text-[11px] font-medium">{f.name}</span>
                            <span className="block text-[10px] text-muted-foreground">{formatAttachmentSize(f.size)}</span>
                          </span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removePendingFile(i)}
                        aria-label={`Remove ${f.name}`}
                        className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border-2 border-background bg-foreground/80 text-background shadow-sm transition-colors hover:bg-destructive hover:text-destructive-foreground"
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex items-end gap-1 p-1.5">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*,application/pdf,.doc,.docx,.txt,.csv,.zip"
                className="hidden"
                onChange={(e) => handleFilesSelected(e.target.files)}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0 rounded-xl text-muted-foreground hover:text-foreground"
                onClick={() => fileInputRef.current?.click()}
                disabled={isSending}
                aria-label="Attach a file"
              >
                <Paperclip className="h-4 w-4" />
              </Button>
              <Textarea
                ref={textareaRef}
                value={reply}
                onChange={(e) => updateReply(e.target.value)}
                rows={1}
                maxLength={4000}
                placeholder={isInternal
                  ? 'Private note for the support team — the customer never sees this…'
                  : ticket.handled_by === 'ai' && ticket.ai_status === 'active'
                    ? 'Write a reply — sending it takes over from BMC Assistant…'
                    : 'Write a reply…'}
                className="min-h-[36px] max-h-[140px] flex-1 resize-none overflow-y-auto border-0 bg-transparent px-1.5 py-2 text-sm shadow-none placeholder:text-muted-foreground/70 focus-visible:ring-0 focus-visible:ring-offset-0"
                onKeyDown={(e) => {
                  // Enter sends. Shift+Enter inserts a newline.
                  if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey && canSend) {
                    e.preventDefault();
                    sendMutation.mutate({ body: reply, internal: isInternal, files: pendingFiles });
                  }
                }}
              />
              <Button
                size="icon"
                disabled={!canSend}
                className={cn(
                  'h-9 w-9 shrink-0 rounded-xl transition-colors disabled:opacity-40',
                  isInternal
                    ? 'bg-amber-500 hover:bg-amber-600'
                    : 'bg-primary hover:bg-primary/90',
                )}
                onClick={() => sendMutation.mutate({ body: reply, internal: isInternal, files: pendingFiles })}
                aria-label={isInternal ? 'Add note' : 'Send reply'}
              >
                {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizontal className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <div className="mt-2 flex items-center justify-between gap-3 px-1">
            <p className="truncate text-[11px] text-muted-foreground">
              {uploading
                ? 'Uploading attachments…'
                : isInternal ? 'Not visible to the customer.'
                : firstAgentMessagePending
                  ? 'Sending the first message moves this ticket to In Progress.'
                  : 'Sending a reply moves this ticket to Waiting on Customer.'}
            </p>
            <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground/70">
              {reply.length > 0
                ? `${reply.length}/4000`
                : <span className="hidden sm:inline">Enter to send, Shift + Enter for a new line</span>}
            </span>
          </div>
          </div>
        </Card>

        {/* Manage — the right-side panel. h-full so it fills the grid row
            (the chat's own explicit height), read as a companion panel rather
            than a card that happens to be squeezed into the remaining space. */}
        <Card className="flex h-full flex-col space-y-3 overflow-y-auto border-border bg-card p-4 shadow-sm">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-slate-600 text-white">
              <SlidersHorizontal className="h-3.5 w-3.5" />
            </span>
            Manage
          </h2>

          <div className="space-y-1.5">
            <Label className="text-xs">Tag</Label>
            <Select
              value={ticket.tags?.[0] ?? NO_TAG}
              onValueChange={(v) => tagMutation.mutate(v === NO_TAG ? null : (v as TicketTag))}
              disabled={tagMutation.isPending}
            >
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_TAG}>None</SelectItem>
                {TICKET_TAGS.map((tag) => (
                  <SelectItem key={tag} value={tag}>{TAG_LABELS[tag]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">The dev team filters the queue on the Technical tag.</p>
          </div>

          <Separator />

          <div className="space-y-1.5">
            <Label className="text-xs">Status</Label>
            <Select
              value={ticket.status}
              onValueChange={(v) => statusMutation.mutate({ to: v as TicketStatus })}
              disabled={isMerged}
            >
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TICKET_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Priority</Label>
            <Select
              value={ticket.priority}
              onValueChange={(v) => priorityMutation.mutate(v as TicketPriority)}
            >
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TICKET_PRIORITIES.map((p) => (
                  <SelectItem key={p} value={p}>{PRIORITY_LABELS[p]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Separator />

          <div className="space-y-1.5">
            <Label className="text-xs">Ticket note (internal)</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Context for whoever picks this up next…"
            />
            <Button
              size="sm"
              variant="outline"
              className="w-full"
              disabled={note.trim() === (ticket.internal_note ?? '').trim() || noteMutation.isPending}
              onClick={() => noteMutation.mutate(note.trim())}
            >
              Save note
            </Button>
          </div>
        </Card>
      </div>

      {/* ── Info cards — Requester / Tracking / Activity stay below the chat,
            reference info you check occasionally rather than mid-conversation
            controls. Each sized to its own content (items-start), never
            stretched to match a taller neighbour. ─────────────────────────── */}
      <div className="grid items-start gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {/* Requester */}
        <Card className="border-border bg-card p-4 shadow-sm">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-violet-600 text-white">
              <UserRound className="h-3.5 w-3.5" />
            </span>
            Requester
          </h2>
          <dl className="space-y-2.5 text-sm">
            <div>
              <dt className="text-xs text-muted-foreground">
                {ticket.source === 'brand' ? 'Brand' : ticket.cross_portal ? 'Creator · app + BMC Coach portal' : ticket.is_coach ? 'Creator · via BMC Coach portal' : 'Creator'}
              </dt>
              <dd className="font-medium">{ticket.display_name || '—'}</dd>
            </div>
            {requesterFullName && requesterFullName !== ticket.display_name && (
              <div>
                <dt className="text-xs text-muted-foreground">Full name</dt>
                <dd className="font-medium">{requesterFullName}</dd>
              </div>
            )}
            <div>
              <dt className="text-xs text-muted-foreground">Email</dt>
              <dd>
                {/* For a brand ticket, ticket.email is the SUBMITTING TEAMMATE's own
                    login email (snapshotted once at creation) — an agency user
                    managing several brands shows the identical address on every
                    one of them. Prefer the brand's own contact email (migration
                    20260921000004) when the ticket is attributed to a brand. */}
                {ticket.brand_contact_email || ticket.email ? (
                  <a
                    href={`mailto:${ticket.brand_contact_email || ticket.email}`}
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    <Mail className="h-3.5 w-3.5" />{ticket.brand_contact_email || ticket.email}
                  </a>
                ) : '—'}
              </dd>
            </div>
            {ticket.source === 'creator' && ticket.roster_creator_id && (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => navigate(`/roster-creators/${ticket.roster_creator_id}`)}
              >
                <ExternalLink className="mr-2 h-3.5 w-3.5" /> View creator activity
              </Button>
            )}
          </dl>
        </Card>

        {/* Tracking — the SLA picture in one card */}
        <Card className="border-border bg-card p-4 shadow-sm">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-sky-600 text-white">
              <Clock3 className="h-3.5 w-3.5" />
            </span>
            Tracking
          </h2>
          <dl className="space-y-2.5 text-sm">
            <div className="flex items-baseline justify-between gap-2">
              <dt className="text-xs text-muted-foreground">Opened</dt>
              <dd className="text-right text-xs">
                {ticket.created_at ? formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true }) : '—'}
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <dt className="text-xs text-muted-foreground">First response</dt>
              <dd className="text-right text-xs font-medium">
                {sla !== null
                  ? formatDuration(sla)
                  : <span className="text-rose-600">pending</span>}
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <dt className="text-xs text-muted-foreground">Last activity</dt>
              <dd className="text-right text-xs">
                {ticket.last_activity_at ? formatDistanceToNow(new Date(ticket.last_activity_at), { addSuffix: true }) : '—'}
              </dd>
            </div>
            {ticket.resolved_at && (
              <div className="flex items-baseline justify-between gap-2">
                <dt className="text-xs text-muted-foreground">Resolved</dt>
                <dd className="text-right text-xs">{format(new Date(ticket.resolved_at), 'MMM d, h:mm a')}</dd>
              </div>
            )}
            {ticket.closed_at && (
              <div className="flex items-baseline justify-between gap-2">
                <dt className="text-xs text-muted-foreground">Closed</dt>
                <dd className="text-right text-xs">{format(new Date(ticket.closed_at), 'MMM d, h:mm a')}</dd>
              </div>
            )}
            {ticket.reopened_count > 0 && (
              <div className="flex items-baseline justify-between gap-2">
                <dt className="text-xs text-muted-foreground">Reopened</dt>
                {/* A high count means we keep resolving without fixing. */}
                <dd className="text-right text-xs font-semibold text-amber-700">
                  {ticket.reopened_count}×
                </dd>
              </div>
            )}
            {ticket.waiting_on_customer_since && (
              <div className="flex items-baseline justify-between gap-2">
                <dt className="text-xs text-muted-foreground">Waiting since</dt>
                <dd className="text-right text-xs">
                  {formatDistanceToNow(new Date(ticket.waiting_on_customer_since), { addSuffix: true })}
                </dd>
              </div>
            )}
            {ticket.follow_up_flagged_at && (
              <div className="flex items-baseline justify-between gap-2">
                <dt className="text-xs text-muted-foreground">Flagged for follow-up</dt>
                {/* Who raised it matters: a 3-day-stale auto-flag reads
                    differently from an agent deliberately escalating it. */}
                <dd className="text-right text-xs font-semibold text-fuchsia-700">
                  {formatDistanceToNow(new Date(ticket.follow_up_flagged_at), { addSuffix: true })}
                  {' · '}
                  {ticket.follow_up_flagged_by === 'system' ? 'auto' : 'by agent'}
                </dd>
              </div>
            )}
          </dl>
        </Card>

        {/* Controls */}
        {/* <Card className="space-y-3 p-4">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-gradient-to-br from-slate-500 to-slate-700 text-white shadow-sm">
              <SlidersHorizontal className="h-3.5 w-3.5" />
            </span>
            Manage
          </h2>

          <div className="space-y-1.5">
            <Label className="text-xs">Status</Label>
            <Select
              value={ticket.status}
              onValueChange={(v) => statusMutation.mutate({ to: v as TicketStatus })}
              disabled={isMerged}
            >
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TICKET_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Priority</Label>
            <Select
              value={ticket.priority}
              onValueChange={(v) => priorityMutation.mutate(v as TicketPriority)}
            >
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TICKET_PRIORITIES.map((p) => (
                  <SelectItem key={p} value={p}>{PRIORITY_LABELS[p]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Separator />

          <div className="space-y-1.5">
            <Label className="text-xs">Ticket note (internal)</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Context for whoever picks this up next…"
            />
            <Button
              size="sm"
              variant="outline"
              className="w-full"
              disabled={note.trim() === (ticket.internal_note ?? '').trim() || noteMutation.isPending}
              onClick={() => noteMutation.mutate(note.trim())}
            >
              Save note
            </Button>
          </div>
        </Card> */}

        {/* Activity timeline — own scroll cap so a long history never drags
            the page length either. */}
        <Card className="flex flex-col border-border bg-card p-4 shadow-sm" style={{ maxHeight: '420px' }}>
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-emerald-600 text-white">
              <History className="h-3.5 w-3.5" />
            </span>
            Activity
          </h2>
          {!events || events.length === 0 ? (
            <p className="text-xs text-muted-foreground">No activity recorded.</p>
          ) : (
            <ol className="space-y-3 overflow-y-auto pr-1">
              {events.map((e, i) => (
                <li key={e.id} className="relative flex gap-3 pl-1">
                  <div className="flex flex-col items-center">
                    <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${
                      e.actor_kind === 'customer' ? 'bg-sky-500'
                        : e.event_type === 'reopened' ? 'bg-amber-500'
                        : 'bg-muted-foreground/50'
                    }`} />
                    {i < events.length - 1 && <span className="mt-1 w-px flex-1 bg-border" />}
                  </div>
                  <div className="min-w-0 flex-1 pb-1">
                    <p className="text-xs font-medium">
                      {EVENT_LABELS[e.event_type] ?? e.event_type}
                      {eventDetail(e) && (
                        <span className="ml-1 font-normal text-muted-foreground">{eventDetail(e)}</span>
                      )}
                    </p>
                    <p className="mt-0.5 text-[10px] text-muted-foreground">
                      {e.actor_name || e.actor_kind} · {formatDistanceToNow(new Date(e.created_at), { addSuffix: true })}
                    </p>
                    {e.note && <p className="mt-1 rounded bg-muted px-2 py-1 text-[11px] text-muted-foreground">{e.note}</p>}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>

      {/* Full-screen image preview — click the backdrop, the X or press Esc to dismiss.
          Portaled to <body> so no ancestor can offset or clip the fixed overlay. */}
      {imageViewer &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] flex flex-col items-center gap-4 bg-black/90 px-6 pb-6 pt-16"
            onClick={() => setImageViewer(null)}
          >
            <button
              type="button"
              onClick={() => setImageViewer(null)}
              aria-label="Close preview"
              className="absolute right-6 top-6 flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex min-h-0 w-full flex-1 items-center justify-center">
              <img
                src={imageViewer.url}
                alt={imageViewer.name || 'Attachment'}
                className="max-h-full max-w-full rounded-lg object-contain shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <a
              href={attachmentDownloadUrl(imageViewer)}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex shrink-0 items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-semibold text-white hover:bg-white/25"
            >
              <Download className="h-4 w-4" /> Download
            </a>
          </div>,
          document.body,
        )}
    </div>
  );
}
